import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { MenuItem, MixedMenuItems } from '@atlassian/universal-create';

import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { END } from '@confluence/navdex/entry-points/NavdexPointType';
import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { useCanCreateSpace } from '@confluence/create-space/entry-points/createSpace';
import { isContentTypeEnabledInCurrentEnv } from '@confluence/content-types-utils/entry-points/isContentTypeEnabledInCurrentEnv';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { fg } from '@confluence/feature-gating';
import { useIsWhiteboardFeatureEnabled } from '@confluence/whiteboard-utils/entry-points/useIsWhiteboardFeatureEnabled';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { expValEquals } from '@confluence/feature-experiments';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery';

import { useSpaceEnabledContentTypes } from '../../CreateContentPopover/useSpaceEnabledContentTypes';
import { useIsCreateMenuLoomRecorderEntryPointEnabled } from '../../CreateContentPopover/CreateContentPopoverItems';

import { useLiveDocItem } from './useLiveDocItem';
import { usePageItem } from './usePageItem';
import { useWhiteboardItem } from './useWhiteboardItem';
import { useDatabaseItem } from './useDatabaseItem';
import { useBlogItem } from './useBlogItem';
import { useEmbedItem } from './useEmbedItem';
import { useSpaceItem } from './useSpaceItem';
import { useLoomItem } from './useLoomItem';
import { useAISmartCreateItem } from './useAISmartCreateItem';

const ANALYTICS_EVENT_SOURCE = 'globalCreateContentPopover';

const messages = defineMessages({
	// eslint-disable-next-line @atlassian/i18n/require-description
	moreFromAtlassian: {
		id: 'create-blank-fabric-page.universal-create.suggestions',
		defaultMessage: 'Suggestions',
	},
});

export type UseMenuItemProps = {
	parentId?: string;
	source: string;
	spaceKey?: string;
	onClick: (actionSubjectId: string, attributes?: Record<string, string>) => void;
	flags?: FlagsStateContainer;
	openFrom?: string;
	excludeContext?: boolean;
};

export type UseMenuItemReturnProps = MenuItem;

export function useMenuItems(
	flags: FlagsStateContainer,
	closeMenu?: () => void,
	openFrom?: string,
): MixedMenuItems {
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const routeDataRef = useRouteDataRef();
	const isOnEditRoute = useIsEditorPage();
	const isLivePage = useIsCurrentPageLive();
	const { isRovoLLMEnabled, accessStatus, isLoggedIn } = useSessionData();

	const handleAnalyticsAndResetStickySearch = useCallback(
		(actionSubjectId: string, attributes?: Record<string, string>) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId,
					source: ANALYTICS_EVENT_SOURCE,
					attributes: {
						navdexPointType: END,
						isOnEditRoute,
						currentRouteName: routeDataRef?.current?.routeName,
						currentRouteIsLive: isLivePage,
						openFrom,
						...attributes,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, isOnEditRoute, routeDataRef, isLivePage, openFrom],
	);

	const onClick = useCallback(
		(actionSubjectId: string, attributes?: Record<string, string>) => {
			handleAnalyticsAndResetStickySearch(actionSubjectId, attributes);
			closeMenu?.();
		},
		[handleAnalyticsAndResetStickySearch, closeMenu],
	);

	const {
		isCreateWhiteboardEnabled,
		isCreateBlogEnabled,
		isCreateLiveDocEnabled,
		isCreateDatabaseEnabled,
		isCreateSmartLinkEnabled,
	} = useSpaceEnabledContentTypes(spaceKey);

	const { canCreateSpace } = useCanCreateSpace();
	const isLoomEntryPointEnabled = useIsCreateMenuLoomRecorderEntryPointEnabled();

	const liveDoc = useLiveDocItem({
		source: 'globalCreateDropdown-liveDoc',
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const page = usePageItem({
		source: 'globalCreateDropdown-page',
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const whiteboard = useWhiteboardItem({
		source: 'globalCreateDropdown-whiteboard',
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const database = useDatabaseItem({
		source: 'globalCreateDropdown-database',
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const blog = useBlogItem({
		source: 'globalCreateDropdown-blog',
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const embed = useEmbedItem({
		source: 'globalCreateDropdown-smartLinkEmbed',
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const loom = useLoomItem({
		source: 'globalCreateDropdown-smartLinkEmbed',
		parentId: contentId,
		spaceKey,
		onClick,
		flags,
	});

	const space = useSpaceItem({
		source: ANALYTICS_EVENT_SOURCE,
		parentId: contentId,
		spaceKey,
		onClick,
	});

	const smartCreate = useAISmartCreateItem({
		source: 'globalCreateDropdown-smartCreate',
		spaceKey,
		onClick,
		openFrom,
		excludeContext: expValEquals('platform_use_llm_space_recommendations', 'isEnabled', true),
	});

	const { isWhiteboardFeatureEnabled } = useIsWhiteboardFeatureEnabled();
	const isOptedOutedOfWhiteboards = isWhiteboardFeatureEnabled('whiteboardOptOut');
	const isWhiteboardsEnabled = isWhiteboardFeatureEnabled('whiteboardsEnabled');

	const whiteboardOptIn = !isOptedOutedOfWhiteboards && isWhiteboardsEnabled;

	const databaseOptIn =
		isContentTypeEnabledInCurrentEnv('database') && !fg('confluence_frontend_databases_opt-out');
	const isAnonymousUser = accessStatus === AccessStatus.ANONYMOUS_ACCESS || !isLoggedIn;

	const getNativeItems = () => {
		const isAifcEnabled = isRovoLLMEnabled && !isAnonymousUser && fg('aifc_create_enabled');

		return {
			items: [
				...(isAifcEnabled ? [smartCreate] : []),
				...(isCreateLiveDocEnabled ? [liveDoc] : []),
				page,
				...(isCreateWhiteboardEnabled && whiteboardOptIn ? [whiteboard] : []),
				...(isCreateDatabaseEnabled && databaseOptIn ? [database] : []),
				...(isCreateBlogEnabled ? [blog] : []),
				...(isCreateSmartLinkEnabled ? [embed] : []),
				...(canCreateSpace ? [space] : []),
			],
		};
	};

	const menuItems: MixedMenuItems = [getNativeItems()];
	const { formatMessage } = useIntl();

	if (isCreateSmartLinkEnabled && isLoomEntryPointEnabled) {
		menuItems.push({
			heading: formatMessage(messages.moreFromAtlassian),
			items: [loom],
		});
	}

	return menuItems;
}
